import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ChatIcon from "@mui/icons-material/Chat";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EditIcon from "@mui/icons-material/Edit";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../firebase/authContext";
import {
  d1GetUserProfile,
  d1SendFriendRequest,
  d1RespondFriendRequest,
  d1RemoveFriend,
} from "../../services/d1Client";

export default function UserCardModal({
  open,
  onClose,
  targetUser,
  onStartChat,
  onFriendshipChanged,
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [actionNotice, setActionNotice] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const targetUid = targetUser?.uid || targetUser?.authorId || targetUser?.userId;
  const fallbackName = targetUser?.displayName || targetUser?.userDisplayName || "主内读者";
  const fallbackPhoto = targetUser?.photoURL || targetUser?.authorPhotoURL || null;

  // Load user profile and friendship status
  const loadProfile = useCallback(async () => {
    if (!targetUid) return;
    setLoading(true);
    setActionNotice("");
    const fallback = {
      uid: targetUid,
      displayName: fallbackName,
      photoURL: fallbackPhoto,
      status: "与神同行，持守真道",
      friendshipStatus: currentUser?.uid === targetUid ? "self" : "none",
      sharedNotesCount: 0,
    };
    try {
      const data = await d1GetUserProfile(targetUid);
      setProfile(data || fallback);
    } catch (err) {
      console.warn("Failed to load user profile:", err);
      setProfile(fallback);
    } finally {
      setLoading(false);
    }
  }, [targetUid, fallbackName, fallbackPhoto, currentUser?.uid]);

  useEffect(() => {
    if (open && targetUid) {
      loadProfile();
    } else {
      setProfile(null);
      setActionNotice("");
    }
  }, [open, targetUid, loadProfile]);

  if (!open) return null;

  const isSelf = Boolean(currentUser && targetUid && currentUser.uid === targetUid);
  const displayName = profile?.displayName || targetUser?.displayName || targetUser?.userDisplayName || "主内读者";
  const photoURL = profile?.photoURL || targetUser?.photoURL || targetUser?.authorPhotoURL;
  const initial = (displayName || "读")[0].toUpperCase();
  const friendshipStatus = isSelf
    ? "self"
    : profile?.friendshipStatus === "self"
    ? "none"
    : profile?.friendshipStatus || "none";

  // Send friend request
  const handleAddFriend = async () => {
    if (!currentUser) {
      setActionNotice("请先登录账号后再添加好友");
      return;
    }
    setActionLoading(true);
    try {
      const res = await d1SendFriendRequest(targetUid);
      if (res && res.status === "accepted") {
        setProfile((prev) => ({ ...prev, friendshipStatus: "accepted" }));
        setActionNotice("双方互相发送了申请，已直接成为主内好友！");
      } else {
        setProfile((prev) => ({ ...prev, friendshipStatus: "pending_sent" }));
        setActionNotice("好友申请已发送，等待对方验证通过！");
      }
      if (onFriendshipChanged) onFriendshipChanged();
    } catch (err) {
      setActionNotice(err.message || "发送申请失败，请稍后重试");
    } finally {
      setActionLoading(false);
    }
  };

  // Respond to incoming friend request
  const handleRespondRequest = async (action) => {
    setActionLoading(true);
    try {
      await d1RespondFriendRequest(profile?.requestId, action, targetUid);
      if (action === "accept") {
        setProfile((prev) => ({ ...prev, friendshipStatus: "accepted" }));
        setActionNotice("已同意好友申请，你们现在是主内好友了！");
      } else {
        setProfile((prev) => ({ ...prev, friendshipStatus: "none" }));
        setActionNotice("已忽略该申请");
      }
      if (onFriendshipChanged) onFriendshipChanged();
    } catch (err) {
      setActionNotice(err.message || "操作失败，请重试");
    } finally {
      setActionLoading(false);
    }
  };

  // Remove friend
  const handleRemoveFriend = async () => {
    if (!window.confirm(`确定要解除与 ${displayName} 的好友关系吗？`)) {
      return;
    }
    setActionLoading(true);
    try {
      await d1RemoveFriend(targetUid);
      setProfile((prev) => ({ ...prev, friendshipStatus: "none" }));
      setActionNotice("已解除好友关系");
      if (onFriendshipChanged) onFriendshipChanged();
    } catch (err) {
      setActionNotice("操作失败，请重试");
    } finally {
      setActionLoading(false);
    }
  };

  // Start chat with user
  const handleStartChat = () => {
    if (onStartChat) {
      onStartChat({
        uid: targetUid,
        displayName,
        photoURL,
      });
    }
    onClose();
  };

  const handleEditProfile = () => {
    navigate("/profile");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
        },
      }}
    >
      {/* Decorative Top Banner */}
      <Box
        sx={{
          height: 100,
          background: "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)",
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          p: 1.5,
        }}
      >
        <Chip
          label="主内同路人"
          size="small"
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.2)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "0.72rem",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        />
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="关闭名片"
          sx={{
            color: "#ffffff",
            bgcolor: "rgba(0, 0, 0, 0.2)",
            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.4)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Card Content Area */}
      <Box sx={{ px: 3, pb: 3, pt: 0, position: "relative" }}>
        {/* Overlapping Avatar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mt: -5, mb: 1.5 }}>
          <Avatar
            src={photoURL}
            alt={displayName}
            sx={{
              width: 76,
              height: 76,
              border: (theme) => `4px solid ${theme.palette.background.paper}`,
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              bgcolor: "primary.main",
              fontSize: "1.8rem",
              fontWeight: 800,
            }}
          >
            {initial}
          </Avatar>

          {/* Friendship Badge */}
          <Box>
            {friendshipStatus === "accepted" && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "16px !important", color: "#2e7d32 !important" }} />}
                label="主内好友"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(46, 125, 50, 0.2)" : "#e8f5e9"),
                  color: "#2e7d32",
                  border: "1px solid rgba(46, 125, 50, 0.3)",
                }}
              />
            )}
            {friendshipStatus === "pending_sent" && (
              <Chip
                icon={<HourglassEmptyIcon sx={{ fontSize: "16px !important", color: "#ed6c02 !important" }} />}
                label="等待对方验证"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(237, 108, 2, 0.2)" : "#fff3e0"),
                  color: "#ed6c02",
                  border: "1px solid rgba(237, 108, 2, 0.3)",
                }}
              />
            )}
            {friendshipStatus === "pending_received" && (
              <Chip
                icon={<NotificationsActiveIcon sx={{ fontSize: "16px !important", color: "#1976d2 !important" }} />}
                label="发来了好友申请"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.2)" : "#e3f2fd"),
                  color: "#1976d2",
                  border: "1px solid rgba(25, 118, 210, 0.3)",
                }}
              />
            )}
            {isSelf && (
              <Chip
                label="我自己"
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Box>
        </Box>

        {/* Name and Status */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {displayName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.75,
              fontStyle: "italic",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f8fafc"),
              p: 1,
              borderRadius: 2,
              borderLeft: "3px solid",
              borderColor: "primary.main",
            }}
          >
            “{profile?.bio ||
              (profile?.status && profile?.status !== "active" && profile?.status !== "disabled"
                ? profile.status
                : "与神同行，持守真道，互勉共进。")}”
          </Typography>
        </Box>

        {/* Stats & Meta info */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: 2.5,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f1f5f9"),
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: "text.secondary", mb: 0.25 }}>
              <MenuBookIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                公开灵修
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
              {profile?.sharedNotesCount || 0} 篇
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: "text.secondary", mb: 0.25 }}>
              <CalendarMonthIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                灵修旅程
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {profile?.createdAt
                ? `${new Date(profile.createdAt).getFullYear()}年${new Date(profile.createdAt).getMonth() + 1}月`
                : "同行者"}
            </Typography>
          </Box>
        </Paper>

        {/* Action Notice Alert */}
        {actionNotice && (
          <Alert
            severity="info"
            onClose={() => setActionNotice("")}
            sx={{ mb: 2, borderRadius: 2, fontSize: "0.85rem" }}
          >
            {actionNotice}
          </Alert>
        )}

        {/* Action Buttons */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isSelf ? (
          <Button
            variant="contained"
            fullWidth
            startIcon={<EditIcon />}
            onClick={handleEditProfile}
            sx={{
              borderRadius: 2.5,
              py: 1,
              fontWeight: 800,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
            }}
          >
            编辑我的个人资料与头像
          </Button>
        ) : friendshipStatus === "accepted" ? (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ChatIcon />}
              onClick={handleStartChat}
              sx={{
                borderRadius: 2.5,
                py: 1,
                fontWeight: 800,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
              }}
            >
              发送私信交流
            </Button>
            <Tooltip title="解除与对方的好友关系">
              <Button
                color="error"
                variant="outlined"
                onClick={handleRemoveFriend}
                disabled={actionLoading}
                aria-label="解除好友"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                sx={{
                  borderRadius: 2.5,
                  px: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                解除
              </Button>
            </Tooltip>
          </Box>
        ) : friendshipStatus === "pending_sent" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              disabled
              startIcon={<HourglassEmptyIcon />}
              sx={{ borderRadius: 2.5, py: 1, fontWeight: 700 }}
            >
              已发送好友申请，等待对方确认
            </Button>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ChatIcon />}
              onClick={handleStartChat}
              sx={{ borderRadius: 2.5, py: 0.8, textTransform: "none", fontWeight: 700 }}
            >
              先发一条问候私信
            </Button>
          </Box>
        ) : friendshipStatus === "pending_received" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 700, textAlign: "center" }}>
              对方发来了好友申请，是否同意？
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={() => handleRespondRequest("accept")}
                sx={{ borderRadius: 2.5, py: 1, fontWeight: 800, textTransform: "none" }}
              >
                {actionLoading ? "处理中…" : "同意好友申请"}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={actionLoading}
                onClick={() => handleRespondRequest("reject")}
                sx={{ borderRadius: 2.5, py: 1, minWidth: 80, textTransform: "none" }}
              >
                忽略
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
              onClick={handleAddFriend}
              sx={{
                borderRadius: 2.5,
                py: 1,
                fontWeight: 800,
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
              }}
            >
              加为好友
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ChatIcon />}
              onClick={handleStartChat}
              sx={{
                borderRadius: 2.5,
                py: 1,
                minWidth: 100,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              发私信
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
